// React Query hooks for the P&L page. Keys are ['admin','pnl',view,params] so
// one invalidate of ['admin','pnl'] (the Refresh button) re-reads everything.
// No polling: the worker recomputes hourly, the page reads its cache.
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  getPnlAccess, getPnlStatus, getPnlStatement, getPnlDaily, getPnlClients,
  getPnlOrders, getPnlSettings, getPnlAccounts,
} from '../../lib/pnlApi'

const STALE = 5 * 60_000
const common = { staleTime: STALE, placeholderData: keepPreviousData, refetchOnWindowFocus: false }

export const PNL_KEY = ['admin', 'pnl']

export function usePnlAccess(enabled = true) {
  return useQuery({
    queryKey: [...PNL_KEY, 'access', {}],
    queryFn: getPnlAccess,
    retry: false,
    staleTime: STALE,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export function usePnlStatus(enabled = true) {
  return useQuery({ queryKey: [...PNL_KEY, 'status', {}], queryFn: getPnlStatus, ...common, enabled })
}

export function usePnlStatement(granularity, anchor, enabled = true) {
  const params = { granularity, anchor }
  return useQuery({
    queryKey: [...PNL_KEY, 'statement', params],
    queryFn: () => getPnlStatement(params),
    ...common,
    enabled: enabled && !!anchor,
  })
}

export function usePnlDaily(from, to, enabled = true) {
  const params = { from, to }
  return useQuery({
    queryKey: [...PNL_KEY, 'daily', params],
    queryFn: () => getPnlDaily(params),
    ...common,
    enabled: enabled && !!from && !!to,
  })
}

export function usePnlClients(from, to, enabled = true) {
  const params = { from, to }
  return useQuery({
    queryKey: [...PNL_KEY, 'clients', params],
    queryFn: () => getPnlClients(params),
    ...common,
    enabled: enabled && !!from && !!to,
  })
}

export function usePnlOrders(params, enabled = true) {
  return useQuery({
    queryKey: [...PNL_KEY, 'orders', params],
    queryFn: () => getPnlOrders(params),
    ...common,
    enabled: enabled && !!params?.date,
  })
}

export function usePnlSettings(enabled = true) {
  return useQuery({ queryKey: [...PNL_KEY, 'settings', {}], queryFn: getPnlSettings, ...common, enabled })
}

export function usePnlAccounts(enabled = true) {
  return useQuery({ queryKey: [...PNL_KEY, 'accounts', {}], queryFn: getPnlAccounts, ...common, enabled })
}
